<template>
  <div>
    <b-row>
      <b-col cols="12">
        <b-card title="Domain Skeleton">
          <b-card-text>
            Export or import the structural metadata of the current domain (datatypes, metatypes, metas, entities, objects, attributes) as JSON. No record data is included.
          </b-card-text>
        </b-card>
      </b-col>
    </b-row>

    <!-- EXPORT -->
    <b-row>
      <b-col cols="12">
        <b-card title="Export">
          <b-card-text>
            Download a JSON file with the full structure of the current domain.
          </b-card-text>
          <b-button
            variant="primary"
            :disabled="exporting"
            @click="doExport"
          >
            <b-spinner
              v-if="exporting"
              small
              class="mr-1"
            />
            {{ exporting ? 'Exporting...' : 'Export Skeleton' }}
          </b-button>

          <div
            v-if="exportResult"
            class="mt-2"
          >
            <b-alert
              show
              variant="success"
            >
              Export complete:
              {{ exportResult.datatypes }} datatypes,
              {{ exportResult.metatypes }} metatypes,
              {{ exportResult.metas }} metas,
              {{ exportResult.entities }} entities,
              {{ exportResult.objects }} objects
            </b-alert>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- IMPORT -->
    <b-row class="mt-1">
      <b-col cols="12">
        <b-card title="Import">
          <b-card-text>
            Upload a previously exported JSON skeleton to apply its structure to the current domain.
            Existing elements (matched by SID) will be skipped.
          </b-card-text>

          <b-form-group label="JSON file">
            <b-form-file
              v-model="importFile"
              accept=".json"
              placeholder="Choose a skeleton JSON file..."
              drop-placeholder="Drop file here..."
              @input="onFileSelected"
            />
          </b-form-group>

          <b-form-group
            v-if="importPreview"
            label="Preview"
          >
            <div
              class="border rounded p-1 bg-light"
              style="max-height: 300px; overflow-y: auto;"
            >
              <small>
                <strong>Domain:</strong> {{ importPreview.domain_name || importPreview.domain }}<br>
                <strong>Exported:</strong> {{ importPreview.exported_at }}<br>
                <strong>Datatypes:</strong> {{ (importPreview.datatypes || []).length }},
                <strong>Metatypes:</strong> {{ (importPreview.metatypes || []).length }},
                <strong>Metas:</strong> {{ (importPreview.metas || []).length }},
                <strong>Entities:</strong> {{ (importPreview.entities || []).length }},
                <strong>Objects:</strong> {{ (importPreview.objects || []).length }}<br>
                <span v-if="importPreview.records">
                  <strong>Records:</strong>
                  <span
                    v-for="(recs, key) in importPreview.records"
                    :key="key"
                  >
                    {{ key }}({{ recs.length }})
                  </span>
                </span>
              </small>
            </div>
          </b-form-group>

          <b-button
            variant="warning"
            :disabled="!importPreview || importing"
            @click="doImport"
          >
            <b-spinner
              v-if="importing"
              small
              class="mr-1"
            />
            {{ importing ? 'Importing...' : 'Import Skeleton' }}
          </b-button>

          <div
            v-if="importResult"
            class="mt-2"
          >
            <b-alert
              show
              variant="success"
            >
              Import complete!
              <ul class="mb-0 mt-1">
                <li
                  v-for="(line, idx) in importResult.counts"
                  :key="idx"
                >
                  {{ line }}
                </li>
              </ul>
            </b-alert>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- CLEAR CACHE -->
    <b-row class="mt-1">
      <b-col cols="12">
        <b-card title="Clear Backend Cache">
          <b-card-text>
            Clear all in-memory caches on the backend (objects, entities, attributes, etc.).
            Use after direct database changes.
          </b-card-text>
          <b-button
            variant="outline-danger"
            :disabled="clearingCache"
            @click="doClearCache"
          >
            <b-spinner
              v-if="clearingCache"
              small
              class="mr-1"
            />
            {{ clearingCache ? 'Clearing...' : 'Clear Cache' }}
          </b-button>
          <b-alert
            v-if="cacheCleared"
            show
            variant="success"
            class="mt-1"
          >
            Backend cache cleared successfully.
          </b-alert>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BCardText, BButton, BSpinner, BAlert,
  BFormGroup, BFormFile,
} from 'bootstrap-vue'
import useJwt from '@/cubus/jwt/useJwt'
import useCubus from '@/cubus/services/useCubus'

export default {
  name: 'CbsDomainSkeleton',
  components: {
    BRow,
    BCol,
    BCard,
    BCardText,
    BButton,
    BSpinner,
    BAlert,
    BFormGroup,
    BFormFile,
  },
  data() {
    return {
      exporting: false,
      exportResult: null,
      importFile: null,
      importPreview: null,
      importJson: null,
      importing: false,
      importResult: null,
      clearingCache: false,
      cacheCleared: false,
    }
  },
  created() {
    useCubus.setPageTitle('Domain Skeleton')
  },
  methods: {
    doExport() {
      this.exporting = true
      this.exportResult = null
      useJwt.query({
        query: {
          mode: 'sync',
          method: 'exportdomainskeleton',
          param: {},
        },
      })
        .then(response => {
          if (response.data.error) {
            useCubus.toastError(this, response.data.error)
          } else {
            const result = response.data.result || response.data
            const skeleton = result.skeleton || result
            if (!skeleton || !skeleton.datatypes) {
              useCubus.toastError(this, 'Unexpected response format')
              return
            }
            const counts = {
              datatypes: (skeleton.datatypes || []).length,
              metatypes: (skeleton.metatypes || []).length,
              metas: (skeleton.metas || []).length,
              entities: (skeleton.entities || []).length,
              objects: (skeleton.objects || []).length,
            }
            this.exportResult = counts

            // Trigger download
            const blob = new Blob([JSON.stringify(skeleton, null, 2)], { type: 'application/json' })
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `${skeleton.domain || 'domain'}_skeleton.json`
            link.click()
            URL.revokeObjectURL(url)
          }
        })
        .catch(error => {
          useCubus.toastError(this, error.message || error)
        })
        .finally(() => {
          this.exporting = false
        })
    },

    onFileSelected(file) {
      this.importPreview = null
      this.importJson = null
      this.importResult = null
      if (!file) return

      const reader = new FileReader()
      reader.onload = e => {
        try {
          const json = JSON.parse(e.target.result)
          if (json.version !== 1 && json.version !== 2) {
            useCubus.toastError(this, `Unsupported skeleton version: ${json.version}`)
            return
          }
          this.importJson = json
          this.importPreview = json
        } catch (err) {
          useCubus.toastError(this, `Invalid JSON: ${err.message}`)
        }
      }
      reader.readAsText(file)
    },

    doImport() {
      if (!this.importJson) return
      this.importing = true
      this.importResult = null
      useJwt.query({
        query: {
          mode: 'sync',
          method: 'importdomainskeleton',
          param: {
            skeleton: this.importJson,
          },
        },
      })
        .then(response => {
          if (response.data.error) {
            useCubus.toastError(this, response.data.error)
          } else {
            const result = response.data.result || response.data
            this.importResult = result
            useCubus.toastMessage(this, 'Domain skeleton imported successfully')
          }
        })
        .catch(error => {
          useCubus.toastError(this, error.message || error)
        })
        .finally(() => {
          this.importing = false
        })
    },
    doClearCache() {
      this.clearingCache = true
      this.cacheCleared = false
      useCubus.clearBackendCache()
        .then(result => {
          if (result && result.error) {
            useCubus.toastError(this, result.error)
          } else {
            this.cacheCleared = true
          }
        })
        .catch(error => {
          useCubus.toastError(this, error.message || error)
        })
        .finally(() => {
          this.clearingCache = false
        })
    },
  },
}
</script>
